// lib/slides/standardText.ts
import { StandardFontEmbedder } from 'pdf-lib';
import { FontNames } from '@pdf-lib/standard-fonts';
import { sanitizeForHelvetica } from './helveticaText';
import anton from './assets/antonMetrics.json';

/** Anton (SIL OFL), the cartouche title face of Thibaut's mock-up; embedded from public/fonts at export. */
export const ANTON = 'Anton' as const;
export type SlideFont = FontNames | typeof ANTON;
const ANTON_ADVANCES: Record<string, number> = anton.advances;

export function standardFontFor(family: 'sans' | 'serif' | 'mono' = 'sans', bold = false, italic = false): FontNames {
  const faces = family === 'serif'
    ? [FontNames.TimesRoman, FontNames.TimesRomanBold, FontNames.TimesRomanItalic, FontNames.TimesRomanBoldItalic]
    : family === 'mono'
      ? [FontNames.Courier, FontNames.CourierBold, FontNames.CourierOblique, FontNames.CourierBoldOblique]
      : [FontNames.Helvetica, FontNames.HelveticaBold, FontNames.HelveticaOblique, FontNames.HelveticaBoldOblique];
  return faces[Number(bold) + 2 * Number(italic)]!;
}

const fonts = new Map<FontNames, StandardFontEmbedder>();
const advances = new Map<FontNames, Map<string, number>>();
const getFont = (name: FontNames) => {
  let font = fonts.get(name);
  if (!font) { font = StandardFontEmbedder.for(name); fonts.set(name, font); }
  return font;
};

// drawText emits unkerned advances; whole-string AFM measurement includes kerning.
export function measureStandardText(text: string, size: number, font: SlideFont = FontNames.Helvetica): number {
  if (font === ANTON) {
    let width = 0;
    for (const character of text) width += ANTON_ADVANCES[character] ?? anton.fallback;
    return width * size;
  }
  let glyphs = advances.get(font);
  if (!glyphs) { glyphs = new Map(); advances.set(font, glyphs); }
  let width = 0;
  for (const character of text) {
    let advance = glyphs.get(character);
    if (advance === undefined) { advance = getFont(font).widthOfTextAtSize(character, 1); glyphs.set(character, advance); }
    width += advance;
  }
  return width * size;
}

// NFC first: macOS file names spell "é" as "e" + a combining accent, which no standard font
// encodes, so "Aménagement" printed as "Ame?nagement".
export const standardDisplayText = (text: string, font: SlideFont = FontNames.Helvetica): string =>
  text.normalize('NFC').replace(/[^\S\r\n]+/g, ' ').split(/\r\n?|\n/).map(line => sanitizeForHelvetica(line, getFont(font === ANTON ? FontNames.Helvetica : font))).join('\n');

export const standardFontStyle = (font: SlideFont) => ({
  fontFamily: font === ANTON ? 'Anton, Impact, sans-serif' : font.startsWith('Times') ? '"Times New Roman", Times, serif' : font.startsWith('Courier') ? '"Courier New", Courier, monospace' : 'Helvetica, Arial, sans-serif',
  fontWeight: font.includes('Bold') ? 700 : 400,
  fontStyle: /Italic|Oblique/.test(font) ? 'italic' as const : 'normal' as const,
  fontKerning: 'none' as const,
  fontVariantLigatures: 'none' as const,
});
